// TODO: deprecated — only consumed by RemotionTimeline (itself deprecated).
// IdeaTimeline does not currently expose a text-animation editing modal,
// so removing this requires either porting the UI to IdeaTimeline or
// confirming the feature is dropped. Safe to delete with the legacy
// timeline cleanup.
import React from "react";
import PropTypes from "prop-types";
import { Row, Col, FormGroup, FormLabel, FormControl } from "react-bootstrap";
import { motion } from "framer-motion";
import i18n from "i18next";
import AppModal from "../../common/modal/AppModal";
import { textAnimationPresets as ANIMATION_PRESETS } from "../../../../common/animation/presets.es6";

const WavyText = ({ text, duration, delay = 0, previewKey }) => {
  const letters = text.split("");
  const _duration = duration === "" ? 0.1 : parseFloat(duration) || 1;
  const _delay = delay === "" ? 0 : parseFloat(delay) || 0;

  return (
    <div style={{ display: "flex" }}>
      {letters.map((letter, index) => (
        <motion.span
          key={`${previewKey}-${index}`}
          initial={{ opacity: 0, x: -30, y: 10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{
            duration: _duration * 0.3,
            delay: _delay + index * (_duration * 0.08),
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          style={{ display: "inline-block" }}
        >
          {letter === " " ? "\u00A0" : letter}
        </motion.span>
      ))}
    </div>
  );
};

const TextAnimationModal = ({
  visible,
  onClose,
  onApply,
  onDelete,
  initialAnimation,
  initialDuration,
  initialDelay,
}) => {
  const [selectedAnimation, setSelectedAnimation] = React.useState(() => {
    if (initialAnimation) {
      const foundPreset = ANIMATION_PRESETS.find((preset) => preset.name === initialAnimation.name);
      return foundPreset || ANIMATION_PRESETS[0];
    }
    return ANIMATION_PRESETS[0];
  });
  const [duration, setDuration] = React.useState(initialDuration || 1);
  const [delay, setDelay] = React.useState(initialDelay || 0);
  const [previewKey, setPreviewKey] = React.useState(0);

  const toggleModal = React.useCallback(
    (e) => {
      let key = e.keyCode ? e.keyCode : e.which;
      if (key === 27 && visible) {
        onClose();
      }
    },
    [visible, onClose]
  );

  const onAnimationSelect = (animation) => {
    setSelectedAnimation(animation);
    setPreviewKey((prev) => prev + 1);
  };

  const onDurationChange = (newDuration) => {
    if (newDuration === "") {
      setDuration("");
      const updatedAnimation = {
        ...selectedAnimation,
        animation: {
          ...selectedAnimation.animation,
          transition: { ...selectedAnimation.animation.transition, duration: 0.1 },
        },
      };
      setSelectedAnimation(updatedAnimation);
      setPreviewKey((prev) => prev + 1);
      return;
    }
    const parsedDuration = parseFloat(newDuration);
    if (isNaN(parsedDuration)) return;

    const clampedDuration = Math.min(Math.max(parsedDuration, 0.1), 10);

    const updatedAnimation = {
      ...selectedAnimation,
      animation: {
        ...selectedAnimation.animation,
        transition: {
          ...selectedAnimation.animation.transition,
          duration: clampedDuration,
        },
      },
    };
    setSelectedAnimation(updatedAnimation);
    setDuration(newDuration);
    setPreviewKey((prev) => prev + 1);
  };

  const onDelayChange = (newDelay) => {
    if (newDelay === "") {
      setDelay("");
      const updatedAnimation = {
        ...selectedAnimation,
        animation: {
          ...selectedAnimation.animation,
          transition: { ...selectedAnimation.animation.transition, delay: 0 },
        },
      };
      setSelectedAnimation(updatedAnimation);
      setPreviewKey((prev) => prev + 1);
      return;
    }
    const parsedDelay = parseFloat(newDelay);
    if (isNaN(parsedDelay)) return;

    const clampedDelay = Math.max(parsedDelay, 0);

    const updatedAnimation = {
      ...selectedAnimation,
      animation: {
        ...selectedAnimation.animation,
        transition: {
          ...selectedAnimation.animation.transition,
          delay: clampedDelay,
        },
      },
    };
    setSelectedAnimation(updatedAnimation);
    setDelay(newDelay);
    setPreviewKey((prev) => prev + 1);
  };

  const playPreview = () => {
    setPreviewKey((prev) => prev + 1);
  };

  React.useEffect(() => {
    window.addEventListener("keyup", toggleModal);
    return () => {
      window.removeEventListener("keyup", toggleModal);
    };
  }, [toggleModal]);

  React.useEffect(() => {
    if (visible) {
      if (initialAnimation) {
        const foundPreset = ANIMATION_PRESETS.find(
          (preset) => preset.name === initialAnimation.name
        );
        if (foundPreset) {
          setSelectedAnimation(foundPreset);
        }
      }
      if (initialDuration !== undefined) {
        setDuration(initialDuration);
      }
      if (initialDelay !== undefined) {
        setDelay(initialDelay);
      }
      setPreviewKey((prev) => prev + 1);
    }
  }, [visible, initialAnimation, initialDuration, initialDelay]);

  const footer = (
    <div className="app-modal__footer" style={{ justifyContent: "space-between" }}>
      <div>
        {onDelete && (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--danger"
            onClick={() => {
              onDelete();
              onClose();
            }}
          >
            {i18n.t("TextAnimations.deleteAnimation") || "Delete Animation"}
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: "12px" }}>
        <button type="button" className="app-modal__btn app-modal__btn--ghost" onClick={onClose}>
          {i18n.t("Cancel")}
        </button>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--primary"
          onClick={() => {
            const _duration =
              duration === "" ? 0.1 : Math.min(Math.max(parseFloat(duration) || 0.1, 0.1), 10);
            const _delay = delay === "" ? 0 : Math.max(parseFloat(delay) || 0, 0);
            onApply(selectedAnimation, _duration, _delay);
            onClose();
          }}
        >
          {i18n.t("TextAnimations.applyAnimation")}
        </button>
      </div>
    </div>
  );

  return (
    <AppModal
      className="textAnimationModal"
      backdrop
      show={visible}
      onClose={onClose}
      title={i18n.t("TextAnimations.title")}
      dialogClassName="text-animation-modal-dialog"
      size="xl"
      footer={footer}
    >
      <Row>
        <Col md={4}>
          <h5>{i18n.t("TextAnimations.chooseAnimation")}</h5>
          <div className="animation-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
            {ANIMATION_PRESETS.map((preset, index) => (
              <div
                key={index}
                className={`animation-item ${
                  selectedAnimation.name === preset.name ? "selected" : ""
                }`}
                onClick={() => onAnimationSelect(preset)}
              >
                <strong>{preset.name}</strong>
              </div>
            ))}
          </div>
        </Col>

        <Col md={8}>
          <Row>
            <Col md={8}>
              <h5>{i18n.t("TextAnimations.preview")}</h5>
              <div
                className="preview-area"
                style={{
                  height: "300px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {selectedAnimation.animation.isWavy ? (
                  <div style={{ fontSize: "24px", fontWeight: "bold", color: "#333" }}>
                    <WavyText
                      text={i18n.t("TextAnimations.sampleText")}
                      duration={duration}
                      delay={delay}
                      previewKey={previewKey}
                    />
                  </div>
                ) : (
                  <motion.div
                    key={previewKey}
                    initial={selectedAnimation.animation.initial}
                    animate={selectedAnimation.animation.animate}
                    transition={{
                      ...selectedAnimation.animation.transition,
                      delay: parseFloat(delay) || 0,
                    }}
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: "#333",
                      whiteSpace: selectedAnimation.name === "Type Writer" ? "nowrap" : "normal",
                      overflow: selectedAnimation.name === "Type Writer" ? "hidden" : "visible",
                    }}
                  >
                    {i18n.t("TextAnimations.sampleText")}
                  </motion.div>
                )}
              </div>
              <div style={{ marginTop: "10px", textAlign: "center" }}>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  onClick={playPreview}
                  style={{ marginRight: "10px" }}
                >
                  <i className="material-icons">replay</i> {i18n.t("TextAnimations.replay")}
                </button>
              </div>
            </Col>

            <Col md={4}>
              <div className="settings-panel">
                <h5>{i18n.t("TextAnimations.settings")}</h5>
                <FormGroup>
                  <FormLabel>{i18n.t("TextAnimations.duration")}</FormLabel>
                  <FormControl
                    type="number"
                    min="0.1"
                    max="10"
                    step="0.1"
                    value={duration}
                    onChange={(e) => onDurationChange(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>{i18n.t("TextAnimations.delay")}</FormLabel>
                  <FormControl
                    type="number"
                    min="0"
                    max="999"
                    step="0.1"
                    value={delay}
                    onChange={(e) => onDelayChange(e.target.value)}
                  />
                  <small className="text-muted">{i18n.t("TextAnimations.delayDescription")}</small>
                </FormGroup>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </AppModal>
  );
};

TextAnimationModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  initialAnimation: PropTypes.object,
  initialDuration: PropTypes.number,
  initialDelay: PropTypes.number,
};

export default TextAnimationModal;
